/**
 * NotFoundPage, hit when someone visits the visitor PWA root URL directly
 * (no QR code in the path). The most likely cause is a QR decoded wrong or
 * a stripped URL; the primary affordance is therefore "try again" (reload)
 * rather than a link out to marketing.
 *
 * Copy is deliberately short: visitors on this page are confused, not
 * browsing. One clear primary action, no decorative chrome.
 */

import { useTranslation } from 'react-i18next';
import { QrCode, AlertCircle, RotateCw, Trophy, ArrowRight } from 'lucide-react';
import { LanguageSwitcher } from '@/components/ui/LanguageSwitcher';

export function NotFoundPage() {
  const { t } = useTranslation('visitor');

  return (
    <div
      className="min-h-[100dvh] flex flex-col items-center justify-center px-5 py-8"
      style={{ background: '#FAFAF7' }}
    >
      <div className="absolute top-4 end-4">
        <LanguageSwitcher compact />
      </div>

      <div className="w-full max-w-[440px] text-center">
        <div className="relative mx-auto w-fit">
          <QrCode className="h-16 w-16 text-[#5A6472]" aria-hidden="true" />
          <AlertCircle
            className="h-6 w-6 absolute -bottom-1 -end-1 rounded-full"
            style={{ color: '#B91C1C', background: '#FAFAF7' }}
            aria-hidden="true"
          />
        </div>

        <h1 className="mt-5 text-[24px] font-bold leading-tight tracking-[-0.02em] text-[#0A0E14]">
          {t('notFound.title')}
        </h1>
        <p className="mt-2 text-[16px] font-medium leading-relaxed text-[#5A6472]">{t('notFound.description')}</p>

        <p className="mt-5 text-[16px] font-medium leading-relaxed text-[#0A0E14]">{t('notFound.hint')}</p>

        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 text-[16px] font-semibold text-white transition-transform active:scale-[0.97]"
          style={{ background: 'linear-gradient(135deg, #0E7C7B 0%, #0A5958 100%)' }}
        >
          <RotateCw className="h-4 w-4" aria-hidden="true" />
          {t('notFound.retry')}
        </button>

        {/* v4 audit: if the sticker itself is damaged, Try Again reloads the
            same invalid URL. Give the visitor a clear next step — close the
            tab, find another sticker, or ask staff. */}
        <p className="mt-4 text-[15px] text-[#5A6472] leading-relaxed">
          {t(
            'notFound.fallbackHint',
            'If this code is damaged, look for another sticker nearby or ask facility staff.',
          )}
        </p>

        {/* Scout CTA, keep, but quieter so it doesn't compete with Scan Again */}
        <a
          href={`${import.meta.env.VITE_SCOUT_URL || 'https://tendedloop-scout.pages.dev'}/login?utm_source=visitor-pwa&utm_medium=notfound-cta`}
          className="mt-8 flex items-center gap-3 rounded-xl border border-dashed p-3 transition-all hover:shadow-sm hover:border-solid group"
          style={{ borderColor: '#40916C' }}
        >
          <div
            className="h-9 w-9 flex-shrink-0 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: 'rgba(64,145,108,0.15)' }}
          >
            <Trophy className="h-5 w-5" style={{ color: '#40916C' }} aria-hidden="true" />
          </div>
          <div className="flex-1 min-w-0 text-start">
            <p className="text-[16px] font-semibold text-[#0A0E14]">{t('cta.scoutTitle')}</p>
            <p className="text-[15px] text-[#5A6472]">{t('cta.scoutMessage')}</p>
          </div>
          <ArrowRight
            className="h-4 w-4 text-[#5A6472] group-hover:translate-x-0.5 transition-transform"
            aria-hidden="true"
          />
        </a>
      </div>
    </div>
  );
}
